import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, ExternalLink, X } from "lucide-react";
import { api, formatCurrency, formatDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PartnerReplyDialog } from "@/components/PartnerReplyDialog";

const POLL_MS = 30000;

function OfferCard({ it, onOpen }) {
  const n = parseInt(it.installments_count, 10) || 1;
  const fraz = (it.frazionamento || "annuale").toLowerCase();
  const total = Number(it.amount) || 0;
  const vehicle = [it.vehicle_brand, it.vehicle_model].filter(Boolean).join(" ");
  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/60 p-3" data-testid={`partner-popup-item-${it.variant_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">🤝 {it.partner_name}{it.company && it.company !== it.partner_name ? <span className="font-medium text-slate-600"> · {it.company}</span> : null}</div>
          <div className="text-xs text-slate-600 mt-0.5">
            👤 <strong>{it.client_name}</strong>
            {it.quote_number && <> · Preventivo <span className="font-mono">{it.quote_number}</span></>}
            {it.offer_number && <> · Offerta <span className="font-mono">{it.offer_number}</span></>}
          </div>
          {(vehicle || it.vehicle_plate) && (
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-sky-50 px-1.5 py-0.5">
              <span>🚗</span>
              {vehicle && <span className="text-[13px] font-semibold text-sky-900">{vehicle}</span>}
              {it.vehicle_plate && <span className="font-mono text-[12px] font-bold tracking-wider bg-white border border-slate-400 rounded px-1.5">{String(it.vehicle_plate).toUpperCase()}</span>}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xl font-extrabold text-emerald-700 tabular-nums" data-testid={`partner-popup-total-${it.variant_id}`}>{formatCurrency(total)}</div>
          <div className="text-[11px] text-slate-600">
            {n > 1 && fraz !== "annuale" ? `${formatCurrency(it.per_rata)} × ${n} rate · ${fraz}` : `Frazionamento ${fraz}`}
          </div>
        </div>
      </div>
      {it.coverages?.length > 0 && (
        <table className="mt-2 w-full text-xs">
          <tbody>
            {it.coverages.map((c, i) => (
              <tr key={i} className="border-t border-emerald-200">
                <td className="py-1 pr-2 text-slate-700">{c.name}{c.kind === "accessory" && <span className="ml-1 text-[10px] text-slate-500">(accessoria)</span>}</td>
                <td className="py-1 text-right tabular-nums font-semibold">{c.premium != null ? formatCurrency(c.premium) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-500">Ricevuta il {formatDateTime(it.created_at)}</span>
        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onOpen(it)} data-testid={`partner-popup-open-${it.variant_id}`}>
          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Apri offerta
        </Button>
      </div>
    </div>
  );
}

function ReplyCard({ r, onRead, onOpen }) {
  return (
    <div className="rounded-lg border-2 border-violet-300 bg-violet-50/60 p-3" data-testid={`partner-popup-reply-${r.inbox_email_id}`}>
      <div className="text-sm font-bold text-slate-900">↩️ {r.from}</div>
      <div className="text-xs text-slate-600 mt-0.5">
        👤 <strong>{r.client_name}</strong>{r.company && <> · {r.company}</>}
        {r.offer_number && <> · Offerta <span className="font-mono">{r.offer_number}</span></>}
        {r.vehicle_plate && <> · <span className="font-mono font-bold">{String(r.vehicle_plate).toUpperCase()}</span></>}
      </div>
      <div className="mt-1 text-xs font-semibold text-violet-900">{r.subject}</div>
      {r.snippet && <div className="text-xs text-slate-700 line-clamp-3">{r.snippet}</div>}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] text-slate-500">Ricevuta il {formatDateTime(r.received_at)}{r.attachments_count > 0 ? ` · 📎 ${r.attachments_count}` : ""}</span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7" onClick={() => onOpen(r)} data-testid={`partner-popup-reply-open-offer-${r.inbox_email_id}`}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Apri offerta
          </Button>
          <Button size="sm" className="h-7 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => onRead(r)} data-testid={`partner-popup-reply-read-${r.inbox_email_id}`}>
            Leggi e rispondi
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PartnerOfferPopup() {
  const [items, setItems] = useState([]);
  const [replies, setReplies] = useState([]);
  const [readReply, setReadReply] = useState(null);
  const closeRead = useCallback(() => setReadReply(null), []);
  const navigate = useNavigate();
  const busy = useRef(false);

  const load = useCallback(async () => {
    if (busy.current || document.hidden) return;
    busy.current = true;
    try {
      const { data } = await api.get("/partner-offers/popup-pending");
      setItems(data?.items || []);
      setReplies(data?.replies || []);
    } catch { /* silenzioso: il popup è best-effort */ }
    finally { busy.current = false; }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = async (ids, rids = []) => {
    setItems((arr) => arr.filter((x) => !ids.includes(x.variant_id)));
    setReplies((arr) => arr.filter((x) => !rids.includes(x.inbox_email_id)));
    try { await api.post("/partner-offers/popup-dismiss", { variant_ids: ids, reply_ids: rids }); } catch { /* noop */ }
  };
  const closeAll = () => dismiss(items.map((x) => x.variant_id), replies.map((x) => x.inbox_email_id));
  const openOffer = (it) => {
    dismiss([it.variant_id]);
    navigate(`/quotes/offers?focus=${it.offer_id}`);
  };
  const openReplyOffer = (r) => {
    dismiss([], [r.inbox_email_id]);
    navigate(`/quotes/offers?focus=${r.offer_id}`);
  };
  const readAndReply = (r) => {
    dismiss([], [r.inbox_email_id]);
    setReadReply(r);
  };
  const total = items.length + replies.length;
  const title = replies.length && !items.length
    ? (replies.length === 1 ? "Nuova risposta dal partner" : `${replies.length} nuove risposte dai partner`)
    : items.length && !replies.length
      ? (items.length === 1 ? "Nuova offerta ricevuta" : `${items.length} nuove offerte ricevute`)
      : `${total} nuovi aggiornamenti dai partner`;

  return (
    <>
    <PartnerReplyDialog emailId={readReply?.inbox_email_id} offerId={readReply?.offer_id} variantId={readReply?.variant_id} onOpenChange={closeRead} />
    <Dialog open={total > 0} onOpenChange={(o) => { if (!o) closeAll(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto border-4 border-emerald-500" data-testid="partner-offer-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <BellRing className="h-5 w-5 animate-bounce" />
            {title}
          </DialogTitle>
          <DialogDescription>{items.length ? "Un partner ha inviato un'offerta online per un tuo preventivo." : "Un partner ha risposto a una tua email su una variante d'offerta."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="partner-offer-popup-list">
          {items.map((it) => <OfferCard key={it.variant_id} it={it} onOpen={openOffer} />)}
          {replies.map((r) => <ReplyCard key={r.inbox_email_id} r={r} onRead={readAndReply} onOpen={openReplyOffer} />)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeAll} data-testid="partner-offer-popup-close">
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default PartnerOfferPopup;
